import React from "react";
import DocumentUploadSection, { DocRequirements, DocSection } from "./DocumentUploadSection";
import {
  refinanceDocumentList,
  generatePLDocumentNames,
  generateBusinessTaxDocumentNames,
  generatePersonalTaxDocumentNames,
  generateGuaranteedBusinessTaxDocumentNames,
  generateGuaranteedPersonalTaxDocumentNames,
} from "../../../constants/general.constant";

const requiredDocs = refinanceDocumentList;

const baseDocRequirements: DocRequirements = {
  "Current Loan Statement": { min: 1, max: 1 },
  "Appraisal (if available)": { min: 0, max: 1 },
  "Phase 1 (if available)": { min: 0, max: 1 },
};

const generatePLDocRequirements = (): DocRequirements => {
  const plDocs: DocRequirements = {};
  const years = generatePLDocumentNames("Refinance YTD");
  years.forEach((docName) => {
    plDocs[docName] = { min: 1, max: 1 };
  });
  return plDocs;
};

const generateBusinessTaxDocRequirements = (): DocRequirements => {
  const taxDocs: DocRequirements = {};
  const years = generateBusinessTaxDocumentNames();
  years.forEach((docName) => {
    taxDocs[docName] = { min: 1, max: 1 };
  });
  return taxDocs;
};

const generatePersonalTaxDocRequirements = (): DocRequirements => {
  const taxDocs: DocRequirements = {};
  const years = generatePersonalTaxDocumentNames();
  years.forEach((docName) => {
    taxDocs[docName] = { min: 1, max: 1 };
  });
  return taxDocs;
};

const generateGuaranteedTaxDocRequirements = (): DocRequirements => {
  const taxDocs: DocRequirements = {};
  const years = [
    ...generateGuaranteedBusinessTaxDocumentNames(),
    ...generateGuaranteedPersonalTaxDocumentNames(),
  ];
  years.forEach((docName) => {
    taxDocs[docName] = { min: 1, max: 1 };
  });
  return taxDocs;
};

// Combine base requirements with dynamic P&L requirements
const docRequirements: DocRequirements = {
  ...baseDocRequirements,
  ...generatePLDocRequirements(),
  ...generateBusinessTaxDocRequirements(),
  ...generatePersonalTaxDocRequirements(),
  ...generateGuaranteedTaxDocRequirements(),
};

// Generate P&L document names for the section
const getPLDocumentNames = (): string[] => {
  return generatePLDocumentNames("Refinance YTD");
};

// Generate Business Tax document names for the section
const getBusinessTaxDocumentNames = (): string[] => {
  return generateBusinessTaxDocumentNames();
};

// Generate Personal Tax document names for the section
const getPersonalTaxDocumentNames = (): string[] => {
  return generatePersonalTaxDocumentNames();
};

// Generate Guaranteed Business Tax document names for the section
const getGuaranteedBusinessTaxDocumentNames = (): string[] => {
  return generateGuaranteedBusinessTaxDocumentNames();
};

// Generate Guaranteed Personal Tax document names for the section
const getGuaranteedPersonalTaxDocumentNames = (): string[] => {
  return generateGuaranteedPersonalTaxDocumentNames();
};

// Create doc sections for P&L with parent title and sub-children
const plSection: DocSection = {
  sectionTitle: "YTD Profit & Loss Statement",
  documents: getPLDocumentNames(),
};

// Create doc sections for Business Taxes
const businessTaxSection: DocSection = {
  sectionTitle: "3 Years of Business Taxes",
  documents: getBusinessTaxDocumentNames(),
};

// Create doc sections for Personal Taxes
const personalTaxSection: DocSection = {
  sectionTitle: "3 Years of Personal Taxes",
  documents: getPersonalTaxDocumentNames(),
};

// Create doc sections for Guaranteed Documents
const guaranteedDocumentSection: DocSection = {
  sectionTitle: "Guaranteed Document",
  sections: [
    {
      sectionTitle: "3 Years of Business Taxes",
      documents: getGuaranteedBusinessTaxDocumentNames(),
    },
    {
      sectionTitle: "3 Years of Personal Taxes",
      documents: getGuaranteedPersonalTaxDocumentNames(),
    },
  ],
};

const RefinanceDocumentsUpload: React.FC<{ customerId: string; customerBusinessId: string }> = ({
  customerId,
  customerBusinessId,
}) => {
  return (
    <DocumentUploadSection
      customerId={customerId}
      customerBusinessId={customerBusinessId}
      requiredDocs={requiredDocs}
      docRequirements={docRequirements}
      title="Refinance Documents Upload"
      docSections={[plSection, businessTaxSection, personalTaxSection, guaranteedDocumentSection]}
    />
  );
};

export default RefinanceDocumentsUpload;

